/**
 *
 * EnvAuthenticatorStepTwo Component
 *
 */

import {
  Button,
  TextField,
  stylize,
} from "@envestnet/envreact-component-library";
import classnames from "classnames";
import React, { useEffect } from "react";
import { SubmissionStateType } from "scenes/EnvAuthenticator/types";
import withIntl from "utils/withIntl";
import { AppTypeEnum } from "./enums";
import messages from "./messages";
import styles, { Styles } from "./styles";

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below

  /** Description of this prop */
  status: SubmissionStateType;
}

export interface EnvAuthenticatorStepTwoProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;

  onCancel: () => void;
  onBack: () => void;
  onSubmitPassCode: (payload: {
    authenticatorType: AppTypeEnum;
    passcode: string;
  }) => void;
  resetSubmissionStatus: () => void;
}

const defaultProps: DefaultProps = {
  status: "INITIATE",
};

type Props = InjectedProps & EnvAuthenticatorStepTwoProps;

const EnvAuthenticatorStepTwo = (props: Props) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,
    onBack,
    onCancel,
    onSubmitPassCode,
    resetSubmissionStatus,
    status,
  } = props;

  const [code, setCode] = React.useState("");
  const [passcodeError, setError] = React.useState<string | null>(null);

  useEffect(() => {
    if (status === "ERROR") {
      setError(formatMessage(messages.invalidCodeErrorMessage));
    } else {
      setError(null);
    }
  }, [status]);

  useEffect(
    () => () => {
      resetSubmissionStatus();
    },
    []
  );

  return (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <div data-testid="env-auth-verify-step-two-code-input">
        <TextField
          label={formatMessage(messages.codeTextField)}
          text={code}
          textStyle={{ fontFamily: "Red Hat Text" }}
          onChangeText={(text) => setCode(text)}
          className={classnames(
            classes.textfield,
            "no-border-radius",
            "header-font-family"
          )}
          errorMessage={passcodeError}
          aria-label="env authenticator passcode textfield"
        />
      </div>
      <div>
        <Button
          buttonStyle="link"
          buttonSize="regular"
          className={classnames(
            classes.linkButton,
            "no-border-radius",
            "header-text-font-family"
          )}
          name={formatMessage(messages.backButtonLabel)}
          onClick={() => onBack()}
          aria-label="back button"
        />
      </div>
      <div>
        <Button
          buttonStyle="primary"
          buttonSize="regular"
          className={classnames(
            classes.verifyButton,
            "no-border-radius",
            "header-text-font-family"
          )}
          name={formatMessage(messages.verifyButtonLabel)}
          onClick={() => {
            onSubmitPassCode({
              authenticatorType: AppTypeEnum.ENV_APP,
              passcode: code,
            });
          }}
          disabled={!code}
          loading={status === "PROCESSING"}
          aria-label="verify button"
        />
      </div>
      <div>
        <Button
          buttonStyle="link"
          buttonSize="regular"
          className={classnames(classes.linkButton, "header-text-font-family")}
          name={formatMessage(messages.cancelButtonLabel)}
          onClick={() => onCancel()}
          aria-label="cancel button"
        />
      </div>
    </div>
  );
};

EnvAuthenticatorStepTwo.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(EnvAuthenticatorStepTwo)
);
